/**
 * Image Comparison Component
 * Allows users to compare two images by dragging a slider
 */

if (!customElements.get('image-comparison')) {
  class ImageComparison extends HTMLElement {
    constructor() {
      super();
      this.direction = this.dataset.direction || 'horizontal';
      this.isHorizontal = this.direction === 'horizontal';
      this.position = 50;
      this.isDragging = false;
      this.animationTriggered = false;
    }

    connectedCallback() {
      this.wrapper = this.querySelector('.image-comparison__wrapper');
      this.handle = this.querySelector('.image-comparison__handle');
      this.beforeContainer = this.querySelector('.image-comparison__before');
      this.afterContainer = this.querySelector('.image-comparison__after');

      if (!this.handle || !this.beforeContainer) return;

      this.bindEvents();
      this.setPosition(this.position);
      this.setupIntersectionObserver();
    }

    bindEvents() {
      // Mouse events
      this.handle.addEventListener('mousedown', this.onDragStart.bind(this));
      document.addEventListener('mousemove', this.onDrag.bind(this));
      document.addEventListener('mouseup', this.onDragEnd.bind(this));

      // Touch events
      this.handle.addEventListener('touchstart', this.onDragStart.bind(this), { passive: false });
      document.addEventListener('touchmove', this.onDrag.bind(this), { passive: false });
      document.addEventListener('touchend', this.onDragEnd.bind(this));

      // Click on wrapper to move handle
      this.wrapper?.addEventListener('click', this.onClick.bind(this));

      // Keyboard support
      this.handle.addEventListener('keydown', this.onKeyDown.bind(this));
      this.handle.setAttribute('tabindex', '0');
      this.handle.setAttribute('role', 'slider');
      this.handle.setAttribute('aria-valuenow', this.position);
      this.handle.setAttribute('aria-valuemin', '0');
      this.handle.setAttribute('aria-valuemax', '100');
      this.handle.setAttribute('aria-label', 'Image comparison slider');
    }

    setupIntersectionObserver() {
      if (this.animationTriggered) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.animationTriggered) {
            this.animationTriggered = true;
            this.animateIn();
            observer.disconnect();
          }
        });
      }, {
        threshold: 0.3
      });

      observer.observe(this);
    }

    animateIn() {
      // Animate the handle from left to center
      this.setPosition(20);
      
      setTimeout(() => {
        this.style.setProperty('--transition-duration', '0.6s');
        this.setPosition(50);
        
        setTimeout(() => {
          this.style.removeProperty('--transition-duration');
        }, 600);
      }, 300);
    }

    onDragStart(e) {
      e.preventDefault();
      this.isDragging = true;
      this.classList.add('is-dragging');
    }

    onDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();

      const rect = this.wrapper.getBoundingClientRect();
      let clientX, clientY;

      if (e.type === 'touchmove') {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      let position;
      if (this.isHorizontal) {
        position = ((clientX - rect.left) / rect.width) * 100;
      } else {
        position = ((clientY - rect.top) / rect.height) * 100;
      }

      this.setPosition(Math.max(0, Math.min(100, position)));
    }

    onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.classList.remove('is-dragging');
    }

    onClick(e) {
      if (this.isDragging) return;
      if (e.target.closest('.image-comparison__handle')) return;

      const rect = this.wrapper.getBoundingClientRect();
      let position;

      if (this.isHorizontal) {
        position = ((e.clientX - rect.left) / rect.width) * 100;
      } else {
        position = ((e.clientY - rect.top) / rect.height) * 100;
      }

      this.style.setProperty('--transition-duration', '0.3s');
      this.setPosition(Math.max(0, Math.min(100, position)));
      
      setTimeout(() => {
        this.style.removeProperty('--transition-duration');
      }, 300);
    }

    onKeyDown(e) {
      const step = e.shiftKey ? 10 : 2;
      let newPosition = this.position;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newPosition = Math.max(0, this.position - step);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newPosition = Math.min(100, this.position + step);
          break;
        case 'Home':
          e.preventDefault();
          newPosition = 0;
          break;
        case 'End':
          e.preventDefault();
          newPosition = 100;
          break;
        default:
          return;
      }

      this.setPosition(newPosition);
    }

    setPosition(position) {
      this.position = position;
      this.style.setProperty('--compare-position', `${position}%`);
      
      // Update image width/height for proper clipping
      if (this.isHorizontal) {
        const imageWidth = (100 / position) * 100;
        this.style.setProperty('--image-width', `${imageWidth}%`);
      } else {
        const imageHeight = (100 / position) * 100;
        this.style.setProperty('--image-height', `${imageHeight}%`);
      }

      // Update ARIA
      this.handle.setAttribute('aria-valuenow', Math.round(position));
    }

    disconnectedCallback() {
      document.removeEventListener('mousemove', this.onDrag.bind(this));
      document.removeEventListener('mouseup', this.onDragEnd.bind(this));
      document.removeEventListener('touchmove', this.onDrag.bind(this));
      document.removeEventListener('touchend', this.onDragEnd.bind(this));
    }
  }

  customElements.define('image-comparison', ImageComparison);
}
